import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import { FiltersFormContext } from './FiltersFormContext';
import { useFormikContext } from 'formik';
import { FILTER_ORIGIN } from '../../../constants/filters';
import {
    Flex,
    Select,
    Typography,
    TreeSelect,
    DatePicker,
    DateRangePicker,
    Checkbox,
    fixedBound,
    resolveDateRange,
} from '@app/ui';
import { parseSelectedPlacementsOptions } from '../../../utils/filterFormHelpers';
import { useParams } from 'react-router-dom';
import { BroadcastTab } from '../../../broadcasts/utils/broadcastsUtils';
import InsightTypeFilter from './InsightTypeFilter';
import dayjs from 'dayjs';
const { SHOW_PARENT } = TreeSelect;
const { RangePicker } = DatePicker;

const RspFiltersRow = ({ filters }) => {
    const { t } = useTranslation();
    const {
        rightsholdersOptions,
        sponsorsOptions,
        placementsOptions,
        placementTypeOptions,
        setFormValue,
        setRightsholders,
        setRightsholdersLeaguePairs,
        hasPremiumPlacementTypes,
        setIsSettingFilters,
        setPlacements,
        isLoading,
        origin,
        isOverridingFilters,
    } = useContext(FiltersFormContext);

    const { activeTab } = useParams();
    const { shouldShowLeagueHierarchy, hasSeveralRightsholders } = filters;
    const isSettingPage = origin === FILTER_ORIGIN.SETTINGS;
    const isAutoInsightsPage = origin === FILTER_ORIGIN.AUTO_INSIGHTS;
    const { values } = useFormikContext();

    const onDateChange = dates => {
        setFormValue('dateSpec')(null);
        if (dates) {
            setFormValue('startDate')(dates[0]);
            setFormValue('endDate')(dates[1]);
        } else {
            setFormValue('startDate')(null);
            setFormValue('endDate')(null);
        }
    };

    const onDateSpecChange = spec => {
        const [startDate, endDate] = resolveDateRange(spec);
        setFormValue('dateSpec')(spec);
        setFormValue('startDate')(startDate);
        setFormValue('endDate')(endDate);
    };

    const dateSpec =
        values.dateSpec ??
        (values.startDate && values.endDate
            ? {
                  start: fixedBound(values.startDate),
                  end: fixedBound(values.endDate),
              }
            : null);
    const isDateRangeDisabled =
        isOverridingFilters ||
        isLoading ||
        (isSettingPage && values.dateRange === 90);

    const rhTreeData = React.useMemo(() => {
        return rightsholdersOptions.map(option => {
            const newNestedOptions =
                option.nested?.map(nestedOption => {
                    return {
                        value: nestedOption.value,
                        title: nestedOption.label,
                    };
                }) ?? null;

            return {
                value: `league_${option.value}`,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [rightsholdersOptions]);

    const plTreeData = React.useMemo(() => {
        return placementsOptions.map(option => {
            const newNestedOptions =
                option.nested?.map(nestedOption => {
                    return {
                        value: `${option.value}-${nestedOption.value}`,
                        title: nestedOption.label,
                    };
                }) ?? null;

            return {
                value: `rh_${option.value}`,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [placementsOptions]);

    const rhs = values.rightsholders?.filter(rh =>
        rightsholdersOptions?.some(o =>
            o.nested?.some(o => parseInt(o.value) === parseInt(rh)),
        ),
    );

    const placementValuesForMultiRH = values.placements?.reduce((acc, plc) => {
        const [rid, pid] = plc.split('-');
        const rh = placementsOptions.find(o => o.value === rid);

        if (!rh) return acc;

        const nested = rh.nested.find(n => `${n.value}` === `${pid}`);
        if (nested) {
            acc.push(plc);
            return acc;
        }

        const exposureOptions = rh.nested.filter(
            n =>
                n.label.includes('Text Exposure') ||
                n.label.includes('Verbal Mention Exposure'),
        );

        if (exposureOptions.length === 0) return acc;

        const exposureOption = exposureOptions.find(o => {
            const tokens = Array.isArray(o.value)
                ? o.value.map(v => `${v}`)
                : `${o.value}`.split(',');
            return tokens.includes(`${pid}`);
        });

        if (exposureOption && !acc.includes(`${rid}-${exposureOption.value}`)) {
            acc.push(`${rid}-${exposureOption.value}`);
        }

        return acc;
    }, []);

    const placementValuesForSingleRH = parseSelectedPlacementsOptions(
        values.placements,
        placementsOptions,
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/display-name, react/no-multi-comp
    const dropdownWithSelectAll = (field, options, set) => menu => (
        <>
            {options.length > 0 && (
                <div
                    style={{ padding: '8px 12px', cursor: 'pointer' }}
                    onClick={() => {
                        set(options.map(o => o.value));
                    }}
                >
                    <Typography.Text
                        style={{
                            fontWeight: 'bold', // Make it bold
                        }}
                    >
                        {t('filters.selectAll')}
                    </Typography.Text>
                </div>
            )}
            {menu}
        </>
    );

    return (
        <Flex wrap="nowrap" gap={16}>
            <Flex vertical flex={1}>
                <Typography.Text className="-filter-title">
                    {t('filters.rightsholders')}
                </Typography.Text>
                {!shouldShowLeagueHierarchy ? (
                    <Select
                        mode="multiple"
                        placeholder={t('filters.selectRightsholders')}
                        options={rightsholdersOptions}
                        value={values.rightsholders || []}
                        onChange={setRightsholders}
                        onDropdownVisibleChange={open =>
                            setIsSettingFilters('rightsholders', open)
                        }
                        optionFilterProp="label"
                        allowClear
                        maxTagCount={2}
                        maxTagTextLength={8}
                        maxTagPlaceholder={`...`}
                        className="-filter-select"
                        autoClearSearchValue={false}
                        dropdownRender={dropdownWithSelectAll(
                            'rightsholders',
                            rightsholdersOptions,
                            setRightsholders,
                        )}
                        disabled={
                            isLoading || rightsholdersOptions.length === 0
                        }
                    />
                ) : (
                    <TreeSelect
                        treeCheckable
                        showCheckedStrategy={SHOW_PARENT}
                        placeholder={t('filters.rightsholder')}
                        treeData={rhTreeData}
                        autoClearSearchValue={false}
                        value={rhs}
                        onChange={selectedValues => {
                            const formattedValues = selectedValues || [];
                            setRightsholdersLeaguePairs(formattedValues);
                        }}
                        onDropdownVisibleChange={open =>
                            setIsSettingFilters('rightsholders', open)
                        }
                        treeNodeFilterProp="title"
                        showSearch
                        allowClear
                        maxTagCount={2}
                        maxTagTextLength={8}
                        maxTagPlaceholder={`...`}
                        className="-filter-select"
                        dropdownRender={dropdownWithSelectAll(
                            'rightsholders',
                            rhTreeData,
                            setRightsholdersLeaguePairs,
                        )}
                        disabled={
                            isOverridingFilters ||
                            isLoading ||
                            rightsholdersOptions.length === 0
                        }
                    />
                )}
            </Flex>
            <Flex vertical flex={1}>
                <Typography.Text className="-filter-title">
                    {t('filters.sponsors')}
                </Typography.Text>
                <Select
                    mode="multiple"
                    placeholder={t('filters.selectSponsors')}
                    options={sponsorsOptions}
                    value={values.sponsors || []}
                    onChange={setFormValue('sponsors')}
                    onDropdownVisibleChange={open =>
                        setIsSettingFilters('sponsors', open)
                    }
                    optionFilterProp="label"
                    allowClear
                    maxTagCount={1}
                    maxTagTextLength={16}
                    maxTagPlaceholder={`...`}
                    style={{
                        maxHeight: '32px',
                        overflow: 'hidden',
                    }}
                    autoClearSearchValue={false}
                    dropdownRender={dropdownWithSelectAll(
                        'sponsors',
                        sponsorsOptions,
                        setFormValue('sponsors'),
                    )}
                    disabled={
                        isOverridingFilters ||
                        isLoading ||
                        sponsorsOptions.length === 0
                    }
                />
            </Flex>
            {hasPremiumPlacementTypes &&
                !isAutoInsightsPage &&
                activeTab !== BroadcastTab.VERBAL_MENTIONS && (
                    <Flex vertical flex={1}>
                        <Typography.Text className="-filter-title">
                            {t('filters.placementType')}
                        </Typography.Text>
                        <Select
                            mode="multiple"
                            placeholder={t('filters.placementType')}
                            options={placementTypeOptions}
                            value={values.placementTypes || []}
                            onChange={setFormValue('placementTypes')}
                            onDropdownVisibleChange={open =>
                                setIsSettingFilters('placementTypes', open)
                            }
                            optionFilterProp="label"
                            allowClear
                            maxTagCount={1}
                            maxTagTextLength={16}
                            maxTagPlaceholder={`...`}
                            className="-filter-select"
                            autoClearSearchValue={false}
                            dropdownRender={dropdownWithSelectAll(
                                'placementTypes',
                                placementTypeOptions,
                                setFormValue('placementTypes'),
                            )}
                            disabled={
                                isOverridingFilters ||
                                isLoading ||
                                placementTypeOptions.length === 0
                            }
                        />
                    </Flex>
                )}
            {activeTab !== BroadcastTab.VERBAL_MENTIONS && (
                <Flex vertical flex={1}>
                    <Typography.Text className="-filter-title">
                        {t('filters.placements')}
                    </Typography.Text>
                    {hasSeveralRightsholders ? (
                        <TreeSelect
                            autoClearSearchValue={false}
                            treeCheckable
                            showCheckedStrategy={SHOW_PARENT}
                            placeholder={t('filters.placements')}
                            treeData={plTreeData}
                            value={placementValuesForMultiRH}
                            onChange={setPlacements}
                            onDropdownVisibleChange={open =>
                                setIsSettingFilters('placements', open)
                            }
                            treeNodeFilterProp="title"
                            showSearch
                            allowClear
                            maxTagCount={1}
                            maxTagTextLength={16}
                            maxTagPlaceholder={`...`}
                            className="-filter-select"
                            dropdownRender={dropdownWithSelectAll(
                                'placements',
                                plTreeData,
                                setPlacements,
                            )}
                            disabled={
                                isOverridingFilters ||
                                isLoading ||
                                placementsOptions.length === 0
                            }
                        />
                    ) : (
                        <Select
                            mode="multiple"
                            placeholder={t('filters.selectPlacements')}
                            options={placementsOptions}
                            value={placementValuesForSingleRH || []}
                            onChange={setFormValue('placements')}
                            onDropdownVisibleChange={open =>
                                setIsSettingFilters('placements', open)
                            }
                            optionFilterProp="label"
                            allowClear
                            className="-filter-select"
                            maxTagCount={2}
                            maxTagTextLength={5}
                            maxTagPlaceholder={`...`}
                            autoClearSearchValue={false}
                            dropdownRender={dropdownWithSelectAll(
                                'placements',
                                placementsOptions,
                                setFormValue('placements'),
                            )}
                            disabled={
                                isOverridingFilters ||
                                isLoading ||
                                placementsOptions.length === 0
                            }
                        />
                    )}
                </Flex>
            )}
            {isAutoInsightsPage && <InsightTypeFilter />}
            {!isAutoInsightsPage && (
                <Flex vertical flex={1}>
                    <Typography.Text className="-filter-title">
                        {t('filters.dateRange')}
                    </Typography.Text>
                    {!isSettingPage ? (
                        <DateRangePicker
                            value={dateSpec}
                            onChange={onDateSpecChange}
                            maxDate={dayjs()}
                            disabled={isDateRangeDisabled}
                            labels={{
                                lastNDays: days =>
                                    t('filters.lastNDays', { days }),
                                today: t('filters.today'),
                                to: t('filters.dateRangeTo'),
                                endBeforeStart: t('filters.endBeforeStart'),
                            }}
                        />
                    ) : (
                        <RangePicker
                            value={[values.startDate, values.endDate]}
                            onChange={onDateChange}
                            maxDate={dayjs()}
                            disabled={isDateRangeDisabled}
                        />
                    )}
                    {isSettingPage && (
                        <Checkbox
                            disabled={
                                isOverridingFilters ||
                                isLoading ||
                                values.dateRange === undefined
                            }
                            checked={values.dateRange === 90}
                            onChange={event => {
                                setFormValue('dateRange')(
                                    event.target.checked ? 90 : null,
                                );
                            }}
                        >
                            {t('filters.last90Days')}
                        </Checkbox>
                    )}
                </Flex>
            )}
        </Flex>
    );
};

RspFiltersRow.propTypes = {
    filters: PropTypes.object.isRequired,
};

export default connect(['filters'])(RspFiltersRow);
